import React, { useState, useEffect } from 'react';
import { useT } from '../i18n';

interface ModelStat {
  name: string;
  foolRate: number;
  totalVotes: number;
}

interface AuthorStat {
  name: string;
  winRate: number;
  totalVotes: number;
}

interface GlobalStats {
  totalVotes: number;
  humanPreferred: number;
  aiPreferred: number;
}

interface LeaderboardProps {
  sessionId: string;
  onBack: () => void;
}

type LeaderboardMode = 'all' | 'literatur' | 'sachtext';
type LeaderboardTab = 'models' | 'authors';

export default function Leaderboard({ sessionId, onBack }: LeaderboardProps) {
  const [models, setModels] = useState<ModelStat[]>([]);
  const [authors, setAuthors] = useState<AuthorStat[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<LeaderboardMode>('all');
  const [tab, setTab] = useState<LeaderboardTab>('models');
  const t = useT();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/votes/leaderboard?sessionId=${sessionId}&mode=${mode}`)
      .then((res) => res.json())
      .then((data) => {
        setModels(data.models || []);
        setAuthors(data.authors || []);
        setGlobalStats(data.globalStats || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sessionId, mode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-stone-400 font-serif">{t('leaderboard.loading')}</div>
      </div>
    );
  }

  const humanPct =
    globalStats && globalStats.totalVotes > 0
      ? Math.round((globalStats.humanPreferred / globalStats.totalVotes) * 100)
      : 50;
  const aiPct = 100 - humanPct;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-2 text-center text-stone-800 italic">
          Leaderboard
        </h2>

        <div className="flex justify-center gap-1 mt-3 mb-2 text-xs font-serif text-stone-400">
          {(['all', 'literatur', 'sachtext'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1 rounded transition-colors ${
                mode === m ? 'text-stone-700 bg-stone-200/60' : 'hover:text-stone-500'
              }`}
            >
              {m === 'all'
                ? t('leaderboard.all')
                : m === 'literatur'
                  ? t('landing.modeLiteratur')
                  : t('landing.modeSachtext')}
            </button>
          ))}
        </div>

        {/* Global Score */}
        {globalStats && globalStats.totalVotes > 0 && (
          <div className="mt-6 mb-8 bg-stone-100 border border-stone-200 rounded-2xl p-5">
            <p className="text-[11px] font-sans font-medium text-stone-500 uppercase tracking-widest mb-3 text-center">
              {t('leaderboard.allVotes')}
            </p>
            <div className="flex items-center gap-4">
              <div className="text-right flex-1">
                <span className="font-display text-2xl font-bold text-emerald-600">
                  {humanPct}%
                </span>
                <p className="text-[11px] text-emerald-600/70 font-sans mt-0.5">
                  {t('results.human')}
                </p>
              </div>
              <div className="flex-[2] h-2 bg-stone-200 rounded-full overflow-hidden flex">
                <div
                  className="bg-emerald-500 h-full transition-all duration-700"
                  style={{ width: `${humanPct}%` }}
                />
                <div
                  className="bg-violet-500 h-full transition-all duration-700"
                  style={{ width: `${aiPct}%` }}
                />
              </div>
              <div className="flex-1">
                <span className="font-display text-2xl font-bold text-violet-600">{aiPct}%</span>
                <p className="text-[11px] text-violet-600/70 font-sans mt-0.5">{t('results.ai')}</p>
              </div>
            </div>
            <p className="text-center text-[11px] text-stone-500 font-sans mt-2">
              {t('leaderboard.totalVotes', { count: globalStats.totalVotes })}
            </p>
          </div>
        )}

        {/* Tab Switcher */}
        <div className="flex justify-center gap-2 mb-4">
          {(['models', 'authors'] as const).map((t2) => (
            <button
              key={t2}
              onClick={() => setTab(t2)}
              className={`px-4 py-1.5 rounded-lg font-display text-sm transition-colors ${
                tab === t2
                  ? 'bg-stone-800 text-white'
                  : 'bg-stone-100 text-stone-500 hover:text-stone-700'
              }`}
            >
              {t2 === 'models' ? t('leaderboard.tabModels') : t('leaderboard.tabAuthors')}
            </button>
          ))}
        </div>

        <p className="text-center text-stone-500 mb-6 text-sm font-serif italic">
          {tab === 'models' ? t('leaderboard.popularity') : t('leaderboard.authorPopularity')}
        </p>

        {tab === 'models' ? (
          models.length === 0 ? (
            <p className="text-center text-stone-400 font-serif">{t('leaderboard.noData')}</p>
          ) : (
            <div className="space-y-3">
              {models.map((model, i) => (
                <div
                  key={model.name}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200"
                >
                  <span className="font-display text-2xl font-bold text-stone-400 w-8 text-right">
                    {i + 1}
                  </span>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-display font-semibold text-stone-700">
                        {model.name}
                      </span>
                      <span className="text-xs text-stone-500 font-sans">
                        {model.totalVotes} {t('leaderboard.votes')}
                      </span>
                    </div>

                    <div className="w-full bg-stone-100 rounded-full h-2">
                      <div
                        className="bg-violet-400 h-2 rounded-full transition-all duration-700"
                        style={{ width: `${model.foolRate}%` }}
                      />
                    </div>

                    <div className="flex justify-end mt-1">
                      <span className="text-[11px] font-sans text-violet-500">
                        {model.foolRate}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : authors.length === 0 ? (
          <p className="text-center text-stone-400 font-serif">{t('leaderboard.noData')}</p>
        ) : (
          <div className="space-y-3">
            {authors.map((author, i) => (
              <div
                key={author.name}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200"
              >
                <span className="font-display text-2xl font-bold text-stone-400 w-8 text-right">
                  {i + 1}
                </span>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display font-semibold text-stone-700">{author.name}</span>
                    <span className="text-xs text-stone-500 font-sans">
                      {author.totalVotes} {t('leaderboard.votes')}
                    </span>
                  </div>

                  <div className="w-full bg-stone-100 rounded-full h-2">
                    <div
                      className="bg-emerald-400 h-2 rounded-full transition-all duration-700"
                      style={{ width: `${author.winRate}%` }}
                    />
                  </div>

                  <div className="flex justify-end mt-1">
                    <span className="text-[11px] font-sans text-emerald-600">
                      {author.winRate}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={onBack}
            className="px-6 py-3 border border-stone-300 hover:border-stone-500 text-stone-500 hover:text-stone-700 font-display rounded-lg transition-all duration-200"
          >
            {t('leaderboard.back')}
          </button>
        </div>
      </div>
    </div>
  );
}
